/* 分析工具 — 台指選擇權籌碼看板（TXGEXBoard）
 *
 *  內嵌「台指選擇權籌碼看板」網站（192.168.11.33:8790，Python stdlib server
 *  伺服的單頁看板：各履約價未平倉、signed GEX、每分鐘重生＋自動 reload）。
 *  整站經 portal 後端唯讀反向代理 `/api/txo-board/*`（同源），前端不直連上游。
 *  整份照 hr-insider.jsx 複製、只改名；主要入口是分類頁／工作台卡片的
 *  「直開新分頁」（hr-shell.jsx openTxoBoardTab），本 iframe 頁保留作
 *  deep-link（#/txo-board）與 Cmd+K 後備。
 *
 *  認證流程（與大神追蹤器的「代理端注入 token」不同 — iframe 內的資源請求
 *  帶不了 Authorization header，所以改用 cookie）：
 *    1. 掛載時先以 portal JWT `POST /api/txo-board/session`（api() 會帶
 *       Authorization header），後端回 204 + Set-Cookie（HttpOnly、
 *       Path=/api/txo-board、效期 8 小時）。
 *    2. cookie 落地後才渲染 iframe（src=/api/txo-board/）—— iframe 內
 *       所有請求（含看板自動 reload）同源自動帶這顆 cookie 過認證。
 *    3. cookie/JWT 過期時 iframe 內容會看到代理回的 401 導引頁 —— 這裡不攔，
 *       使用者可回本頁按「重試」重新換一顆 session cookie。
 */

function TxoBoardPage() {
  // 'loading' = 換 session 中；'ready' = cookie 已落地、渲染 iframe；'error' = 換發失敗
  const [phase, setPhase] = React.useState('loading');
  const [errDetail, setErrDetail] = React.useState('');

  // 換發 session cookie —— 掛載時打一次；失敗後「重試」也走這條（能重新換發）
  const initSession = React.useCallback(() => {
    let alive = true;
    setPhase('loading');
    setErrDetail('');
    // 成功回 204（無 body）→ api() resolve null；非 2xx 會 throw { status, detail }
    api('POST', '/txo-board/session')
      .then(() => { if (alive) setPhase('ready'); })
      .catch((e) => {
        if (!alive) return;
        setErrDetail((e && e.detail) || '網路連線失敗');
        setPhase('error');
      });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => initSession(), [initSession]);

  return (
    <>
      <PageHeader
        eyebrow="分析工具 · TXO GEX"
        title="台指選擇權籌碼看板"
        italic="台指選擇權籌碼看板 — 各履約價未平倉與 signed GEX，每分鐘自動更新，經 portal 代理"
        action={
          // 同源開新分頁 —— session cookie（Path=/api/txo-board）會自動帶上
          <Button
            variant="ghost" size="sm" icon="ext"
            onClick={() => window.open('/api/txo-board/', '_blank', 'noopener')}
          >在新分頁開啟</Button>
        }
      />

      {phase === 'loading' && (
        // 沿用站內 loading 樣式（Spinner + 提示、固定高度不跳版）
        <div style={uiStyle({
          height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: HR.surfaceAlt, borderRadius: 6, color: HR.inkFaint, fontSize: FontSizes.xs,
        }, { background: GxT.color.card, borderRadius: GxT.radius.card, color: GxT.color.onV, fontSize: 14 })}>
          <Spinner size={16} /> 連線選擇權籌碼看板中…
        </div>
      )}

      {phase === 'error' && (
        <Card padding="0">
          <EmptyState
            icon="x" title="選擇權籌碼看板連線失敗"
            hint={errDetail ? `無法建立代理連線（${errDetail}），請重試或聯繫管理員。` : '無法建立代理連線，請重試或聯繫管理員。'}
            action={<Button variant="primary" size="sm" icon="refresh" onClick={initSession}>重試</Button>}
          />
        </Card>
      )}

      {phase === 'ready' && (
        // 高度撐滿內容區：100vh 扣掉 WorkspaceHeader + main padding + PageHeader
        // （作法比照 hr-insider.jsx；本頁沒有工具列所以扣得少一點）
        <iframe
          src="/api/txo-board/"
          title="台指選擇權籌碼看板"
          style={uiStyle({
            width: '100%', height: 'calc(100vh - 260px)', minHeight: 480,
            border: 0, borderRadius: 6, background: HR.surface, display: 'block',
          }, { borderRadius: GxT.radius.card })}
        />
      )}
    </>
  );
}
